// Welcome screen. With a local vault → "unlock". With none, the cloud
// situation decides (decideCloudMode):
//   not signed in to Puter → "signin" (offer to sign in, then look again)
//   signed in, remote vault → "open-cloud"
//   signed in, no remote    → "create"
//
// The "signin" branch exists because a fresh device (e.g. opening
// vault.puter.com cold on a phone) has no ambient Puter session, so
// isSignedIn() is false. Without it we'd conflate "not signed in" with
// "no cloud vault" and silently drop to create, hiding an existing Safe.
//
// Why open-cloud at the welcome stage: doing it post-createVault creates an
// empty local vault that PuterSync immediately first-pushes to Puter, which
// races against any later "real" push and can transiently expose an empty
// blob to other devices joining at the same moment. Opening directly from
// the cloud blob avoids that empty intermediate state entirely.

import { el, clear } from './dom.js';
import { PuterSync } from '../sync/puter-sync.js';

export function renderWelcome(container, { vault, onUnlocked }) {
  clear(container);
  container.append(el('div', { class: 'welcome' }, [build(vault, onUnlocked)]));
}

function build(vault, onUnlocked) {
  const card = el('div', { class: 'welcome-card' }, []);
  let mode = null;        // 'unlock' | 'signin' | 'open-cloud' | 'create'
  let remoteMeta = null;  // populated in open-cloud mode

  // A local vault is the common case — decide on it alone and render the
  // unlock form (focusing the password field) immediately, without waiting on
  // the remote-vault network probe. Only when there's no local vault do we
  // show a loading state and decide the cloud mode (signin/open-cloud/create).
  vault.hasVault().then((hasLocal) => {
    if (hasLocal) { mode = 'unlock'; render(); return; }
    renderLoading();
    decideCloudMode(vault).then(applyCloudDecision);
  });

  function applyCloudDecision({ mode: m, remoteMeta: rm = null }) {
    mode = m;
    remoteMeta = rm;
    render();
  }

  function render() {
    clear(card);
    if (mode === 'unlock') renderUnlock();
    else if (mode === 'signin') renderSignin();
    else if (mode === 'open-cloud') renderOpenCloud();
    else renderCreate();
  }

  function renderLoading() {
    clear(card);
    card.append(
      el('h1', {}, ['Vault']),
      el('p', { class: 'subtitle' }, ['Checking for your Safe…']),
    );
  }

  function shake() {
    card.classList.remove('shake');
    void card.offsetWidth;
    card.classList.add('shake');
  }

  function renderUnlock() {
    const error = el('div', { class: 'error' });
    const pw = el('input', { type: 'password', placeholder: 'Master password', autocomplete: 'current-password', autofocus: true });
    const submit = el('button', { type: 'submit', class: 'primary' }, ['Unlock']);

    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      error.textContent = '';
      submit.disabled = true;
      submit.textContent = 'Unlocking…';
      try {
        await vault.unlock(pw.value);
        onUnlocked();
      } catch (err) {
        shake();
        error.textContent = 'Wrong password';
        pw.select();
      } finally {
        submit.disabled = false;
        submit.textContent = 'Unlock';
      }
    } }, [
      pw,
      error,
      el('div', { class: 'actions' }, [submit]),
    ]);

    const resetLink = el('button', {
      type: 'button',
      class: 'ghost',
      style: { marginTop: '12px', fontSize: '12px', color: 'var(--muted-2)', width: '100%' },
      onclick: async () => {
        if (!confirm('Reset Safe? This permanently deletes every entry on this device. There is no undo.')) return;
        if (!confirm('Are you absolutely sure? Type your master password into the field above to keep your Safe; click OK to destroy it.')) return;
        resetLink.disabled = true;
        resetLink.textContent = 'Resetting…';
        try {
          await vault.destroy();
          // Re-decide in case Puter has a vault we should now offer to open
          // (or the user needs to sign in to look for one).
          applyCloudDecision(await decideCloudMode(vault));
        } catch (err) {
          error.textContent = `Reset failed: ${err?.message ?? err}`;
          resetLink.disabled = false;
          resetLink.textContent = 'Reset Safe (delete all local data)';
        }
      },
    }, ['Reset Safe (delete all local data)']);

    card.append(
      el('h1', {}, ['Vault']),
      el('p', { class: 'subtitle' }, ['Enter your master password to unlock.']),
      form,
      resetLink,
    );
    setTimeout(() => pw.focus(), 0);
  }

  function renderSignin() {
    const error = el('div', { class: 'error' });
    const signin = el('button', { type: 'button', class: 'primary' }, ['Sign in to Puter']);
    signin.onclick = async () => {
      error.textContent = '';
      signin.disabled = true;
      signin.textContent = 'Signing in…';
      try {
        await globalThis.puter.auth.signIn();
        renderLoading();
        applyCloudDecision(await decideCloudMode(vault));
      } catch (err) {
        // Popup dismissed or sign-in failed — stay here so the user can retry.
        shake();
        error.textContent = 'Sign-in was cancelled or failed.';
        signin.disabled = false;
        signin.textContent = 'Sign in to Puter';
      }
    };

    const createLink = el('button', {
      type: 'button',
      class: 'ghost',
      style: { marginTop: '12px', fontSize: '12px', color: 'var(--muted-2)', width: '100%' },
      onclick: () => { mode = 'create'; render(); },
    }, ['Create a new Safe instead']);

    card.append(
      el('h1', {}, ['Vault']),
      el('p', { class: 'subtitle' }, [
        'No Safe on this device. If you already have one in your Puter account, sign in to open it.',
      ]),
      el('div', { class: 'actions' }, [signin]),
      error,
      createLink,
    );
  }

  function renderOpenCloud() {
    const error = el('div', { class: 'error' });
    const pw = el('input', { type: 'password', placeholder: 'Master password', autocomplete: 'current-password' });
    const submit = el('button', { type: 'submit', class: 'primary' }, ['Open from Puter']);

    const meta = remoteMeta;
    const subtitleParts = ['A Safe was found in your Puter account.'];
    if (meta?.modifiedAt) subtitleParts.push(`Last updated ${formatTime(meta.modifiedAt)}.`);
    if (meta?.size) subtitleParts.push(`(${formatBytes(meta.size)})`);

    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      error.textContent = '';
      if (!pw.value) { error.textContent = 'Enter the master password.'; shake(); return; }
      submit.disabled = true;
      submit.textContent = 'Opening…';
      try {
        const sync = new PuterSync({ vault, puter: globalThis.puter });
        await sync.joinFromRemote(pw.value);
        onUnlocked();
      } catch (err) {
        shake();
        if (err?.name === 'DecryptionError') {
          error.textContent = 'Wrong password for this cloud Safe.';
          pw.select();
        } else {
          error.textContent = err?.message ?? String(err);
        }
        submit.disabled = false;
        submit.textContent = 'Open from Puter';
      }
    } }, [
      pw,
      error,
      el('div', { class: 'actions' }, [submit]),
    ]);

    card.append(
      el('h1', {}, ['Open Safe']),
      el('p', { class: 'subtitle' }, [subtitleParts.join(' ')]),
      form,
    );
    setTimeout(() => pw.focus(), 0);
  }

  function renderCreate() {
    const error = el('div', { class: 'error' });
    const pw1 = el('input', { type: 'password', placeholder: 'New master password', autocomplete: 'new-password' });
    const pw2 = el('input', { type: 'password', placeholder: 'Confirm master password', autocomplete: 'new-password' });
    const submit = el('button', { type: 'submit', class: 'primary' }, ['Create Safe']);

    const form = el('form', { onsubmit: async (e) => {
      e.preventDefault();
      error.textContent = '';
      if (pw1.value.length < 8) { error.textContent = 'Master password must be at least 8 characters'; shake(); return; }
      if (pw1.value !== pw2.value) { error.textContent = 'Passwords do not match'; shake(); pw2.select(); return; }
      submit.disabled = true;
      submit.textContent = 'Creating…';
      try {
        await vault.createVault(pw1.value);
        onUnlocked();
      } catch (err) {
        error.textContent = err.message ?? String(err);
        shake();
      } finally {
        submit.disabled = false;
        submit.textContent = 'Create Safe';
      }
    } }, [
      pw1,
      pw2,
      error,
      el('div', { class: 'actions' }, [submit]),
    ]);

    card.append(
      el('h1', {}, ['Create Safe']),
      el('p', { class: 'subtitle' }, ['Pick a strong master password. You cannot recover it — write it down somewhere safe.']),
      form,
    );
    setTimeout(() => pw1.focus(), 0);
  }

  return card;
}

// Decide the welcome mode when there is NO local vault. Distinguishes
// "not signed in to Puter" (→ signin: offer to sign in and look) from
// "signed in but no cloud vault" (→ create), so an existing cloud Safe is
// never silently hidden behind the create flow on a fresh device. Returns
// { mode: 'signin' | 'open-cloud' | 'create', remoteMeta? }. Exported for
// unit testing.
export async function decideCloudMode(vault, puter = globalThis.puter) {
  if (!puter) return { mode: 'create' };
  let signedIn = false;
  try {
    signedIn = !!(await puter.auth?.isSignedIn?.());
  } catch {
    // Auth check failed — treat as signed-out so the user can still choose
    // to sign in rather than being dropped straight into create.
    return { mode: 'signin' };
  }
  if (!signedIn) return { mode: 'signin' };
  const remote = await probeRemoteVault(vault, puter);
  return remote ? { mode: 'open-cloud', remoteMeta: remote } : { mode: 'create' };
}

// Probe Puter for an existing vault.meta.json. Assumes the caller already
// confirmed a signed-in session. Returns the meta object on success, null in
// every failure mode (network error, not found) — never blocks the welcome
// screen on the cloud being reachable.
async function probeRemoteVault(vault, puter = globalThis.puter) {
  if (!puter) return null;
  try {
    const sync = new PuterSync({ vault, puter });
    return await sync.readRemoteMeta();
  } catch (err) {
    console.warn('cloud vault probe failed', err);
    return null;
  }
}

function formatTime(ms) {
  if (!ms) return '';
  return new Date(ms).toLocaleString();
}
function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
